import type { EditSource } from "./draft-store";
import { changedFields, stillThere } from "./hand-set";
import type { PostDraft, PostShift } from "./model";
import type { NewShiftDefaults } from "./useNewShiftDefaults";

// The fields the page filled by itself, which nobody has given since. The chat treats them as not yet said.

const PAGE_FIELDS: Record<keyof NewShiftDefaults, string> = {
  address_id: "where",
  parking_expense_type: "parking",
  parking_instructions: "arrival",
  manager: "manager",
};

/** The card with these fields now the page's own. */
export const seeded = (shift: PostShift, ...keys: Array<string>): PostShift =>
  keys.length === 0 ? shift : { ...shift, defaults: [...new Set([...(shift.defaults ?? []), ...keys])] };

/** The card with these fields given: someone set them, even to the value they held. */
export const given = (shift: PostShift, ...keys: Array<string>): PostShift =>
  shift.defaults?.some((key) => keys.includes(key))
    ? { ...shift, defaults: shift.defaults.filter((key) => !keys.includes(key)) }
    : shift;

/** A new card filled with the location's venue, parking, arrival and manager, each held as the page's. */
export const withPageDefaults = (shift: PostShift, values: Partial<NewShiftDefaults>): PostShift =>
  seeded(
    { ...shift, ...values },
    ...Object.entries(values).flatMap(([field, value]) =>
      value == null ? [] : [PAGE_FIELDS[field as keyof NewShiftDefaults]],
    ),
  );

/**
 * The draft after a hand edit: a field it changed is given, and a role it added holds the page's count and a saved position's
 * wage. The chat's own edits say what they give themselves (page-ops), since one can give a field the value it held.
 */
export const withGivenFields = (before: PostDraft, after: PostDraft, source: EditSource): PostDraft => {
  if (source !== "hand") return after;
  let changed = false;
  const shifts = after.shifts.map((shift) => {
    const was = before.shifts.find(({ key }) => key === shift.key);
    if (!was) return shift;
    const fields = changedFields(was, shift);
    const added = shift.positions.filter(({ key }) => !was.positions.some((old) => old.key === key));
    const next = seeded(
      { ...shift, defaults: (shift.defaults ?? []).filter((key) => !fields.includes(key) && stillThere(key, shift)) },
      ...added.flatMap((role) => [
        ...(role.count === 1 ? [`count:${role.key}`] : []),
        ...(role.template_id && role.pay_rate != null ? [`pay:${role.key}`] : []),
      ]),
    );
    if ((next.defaults ?? []).join() === (shift.defaults ?? []).join()) return shift;
    changed = true;
    return next.defaults?.length ? next : { ...next, defaults: undefined };
  });
  return changed ? { ...after, shifts } : after;
};
